import React, { useState } from "react";
import moment from "moment";
import TaskInterface from "./Interface";
import View from "./view";
import TaskActions from "./Actions";
import { useNotify } from "@/hooks/useNotify";
import { ORDERS_NS as ns } from "@/i18n/ns";
import TaskTimers from "./Timers";
import TaskServices from "./Services";
import { useButtonsSelectors } from "./hooks/useButtonsSelectors";
import { useNow } from "./hooks/useNow";
import { useAsyncCallback } from "@/hooks/useAsyncCallback";
import { TasksAPI } from "@/api/tasks";
import { copyToClipboard, openLinkInSameTab } from "@/utils";
import TaskCustomFields from "./CustomFields";
import PT from "prop-types";
import Badges from "./Badges";
import { Translate } from "@/i18n/utils";
import { connect } from "react-redux";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Effects as MessengerEffects } from "@/store/messenger/effects";
import ClientExcerpt from "./ClientExcerpt";
import { TasksService } from "@/services/tasks";
import { useMut } from "@/hooks/useMut";
import { Actions as ModalActions } from "@/store/modals/slice";
import { EXTERNAL_NOTIFICATION_EVENTS_MAP } from "@/misc/externalNotificationEvents";
import { analyticsAction } from "@/utils/analytics";
import { useWarehouseTxnEditor } from "@/hooks/useWarehouseTxnEditor";
import { useRequestTaskPayout } from "@/hooks/useRequestTaskPayout";
import { TaskClaimsService } from "@/services/taskClaims";

const connector = connect(
  (state) => ({
    authUserId: UserSelectors.getUserId(state),
    currency: UserSelectors.getCurrency(state),
    authMasterId: UserSelectors.getMasterId(state),
    locale: UserSelectors.getLocale(state),
  }),
  {
    sendMessage: MessengerEffects.createMessageOutsideMessenger,
    sendExternalNotification: ModalActions.openSendNotification,
  }
);
const TaskItem = ({ t, task, ...props }) => {
  const now = useNow();
  const { notify, notifyError } = useNotify();
  const [activeAction, setActiveAction] = useState(null);
  const isTaskMaster = task.master === props.authMasterId;
  const isTaskConsumer = task.consumer === props.authUserId;
  const buttonsAppear = useButtonsSelectors({
    task,
    isTaskMaster,
    now,
    isTaskConsumer,
  });
  const copyIdToClipboard = () => {
    copyToClipboard(task._id);
    notify(t(`${ns}:id_copied`));
  };
  const requestPayout_ = useRequestTaskPayout();
  const requestPayout = () => requestPayout_({ task: task._id });
  const isPayWaiting = task.status === "PAY_WAITING";
  const toggleAction = (type) => {
    if (activeAction === type) {
      setActiveAction(null);
    } else {
      setActiveAction(type);
    }
  };
  const closeAction = () => setActiveAction(null);
  const requestPhotoField = isTaskConsumer
    ? "client_requested_photo"
    : "master_requested_photo";
  const requestPhotoMut = useMut(
    () =>
      TasksService.requestPhoto(task, {
        field: requestPhotoField,
      }),
    {
      t,
      notifyOnError: true,
      onSuccess() {
        notify(t(`${ns}:request_photo_success`));
        if (props.onTaskUpdate) {
          props.onTaskUpdate({
            id: task._id,
            updates: {
              [requestPhotoField]: true,
            },
          });
        }
      },
    }
  );
  const acceptMut = useMut(() => TasksService.acceptTask(task), {
    t,
    notifyOnError: true,
    onSuccess() {
      notify(t(`${ns}:accept_success`));
      if (props.onTaskUpdate) {
        props.onTaskUpdate({
          id: task._id,
          updates: {
            status: task.safe ? "PAY_WAITING" : "ACCEPTED",
          },
        });
      }
    },
  });
  const noteField = isTaskConsumer ? "client_note" : "master_note";
  const [createNote] = useAsyncCallback({
    callback: (payload) =>
      TasksService.createNote(task, payload, { field: noteField }),
    onSuccess: (_res, payload) => {
      if (props.onTaskUpdate) {
        props.onTaskUpdate({
          id: task._id,
          updates: {
            [noteField]: payload.text,
          },
        });
      }
      closeAction();
    },
    onError: (e) => notifyError(Translate.serverError(t, e)),
  });
  const [removeNote] = useAsyncCallback({
    callback: () => TasksService.removeNote(task, { field: noteField }),
    onSuccess: () => {
      if (props.onTaskUpdate) {
        props.onTaskUpdate({
          id: task._id,
          updates: {
            [noteField]: undefined,
          },
        });
      }
      closeAction();
    },
    onError: (e) => notifyError(Translate.serverError(t, e)),
  });
  const finishMut = useMut(() => TasksService.finishTask(task), {
    notifyOnError: true,
    t,
    onSuccess() {
      notify(t(`${ns}:finish_success`));
      if (props.onTaskUpdate) {
        props.onTaskUpdate({
          id: task._id,
          updates: {
            status: "DONE",
          },
        });
      }
    },
  });
  const requestReviewMut = useMut(() => TasksService.requestReview(task), {
    notifyOnError: true,
    t,
    toast: () => t(`${ns}:request_review_success`),
  });
  const removeMut = useMut(() => TasksService.removeTask(task), {
    t,
    confirm: true,
    notifyOnError: true,
    onSuccess() {
      notify(t(`${ns}:remove_success`));
      if (props.onTaskRemove) {
        props.onTaskRemove(task._id);
      }
    },
  });
  const removeDisputeMut = useMut(
    () => TaskClaimsService.removeTaskClaim(task.claim),
    {
      t,
      confirm: true,
      notifyOnError: true,
      toast: () => t(`${ns}:remove_dispute_success`),
    }
  );
  const [cancelTask] = useAsyncCallback({
    callback: (payload) => TasksService.cancelTask(task, payload),
    onSuccess: (_res, payload) => {
      notify(t(`${ns}:cancel_success`));
      if (props.onTaskCancel) {
        props.onTaskCancel(task._id);
      } else if (props.onTaskUpdate) {
        props.onTaskUpdate({
          id: task._id,
          updates: {
            status: "CANCELLED",
            cancel_reason: payload.reason,
          },
        });
      }
      closeAction();
    },
    onError: (e) => notifyError(Translate.serverError(t, e)),
  });
  const payLinkMut = useMut(() => TasksAPI.getPayLink(task._id), {
    t,
    notifyOnError: true,
    onSuccess: ({ payLink }) => openLinkInSameTab(payLink),
  });
  const copyMut = useMut(() => TasksService.copyTask(task), {
    t,
    notifyOnError: true,
  });
  const sendMessage = async (text) => {
    const receiver = isTaskConsumer ? task.master_meta?._id : task.consumer;
    await props.sendMessage(receiver, text);
  };
  const openClientProfile = () => props.openClientProfile(task.client);
  const isPaid = task.status === "PAID";
  const isAccepted = task.status === "ACCEPTED";
  const isPending = task.status === "PENDING";
  const isDisputeAllowed =
    isPaid &&
    moment(task.end_date).isBefore(now) &&
    moment(task.dispute_deadline).isAfter(now);
  const note = isTaskConsumer ? task.client_note : task.master_note;
  const remind = () => {
    analyticsAction({
      category: "records",
      action: "rec_remind",
    });
    props.sendExternalNotification({
      task: task._id,
      event: EXTERNAL_NOTIFICATION_EVENTS_MAP.NOTIFY_CLIENT,
    });
  };
  const openWarehouseTxnEditor_ = useWarehouseTxnEditor();
  const openWarehouseTxnEditor = () =>
    openWarehouseTxnEditor_({
      defaultDirection: "out",
      saloon: task.saloon,
      master: task.master,
      defaultTask: task._id,
    });
  return (
    <>
      <View
        task={task}
        note={note}
        t={t}
        viewMode={props.viewMode}
        className={props.className}
        isCustom={task.custom}
        Services={<TaskServices services={task.services} />}
        CustomFields={
          <TaskCustomFields
            isTaskConsumer={isTaskConsumer}
            customFields={task.custom_fields}
            t={t}
          />
        }
        Badges={
          <Badges
            isTaskConsumer={isTaskConsumer}
            openClientProfile={openClientProfile}
            viewMode={props.viewMode}
            task={task}
            t={t}
          />
        }
        Timers={
          <TaskTimers
            t={t}
            now={now}
            isSafe={Boolean(task.safe)}
            isPaid={isPaid}
            startDate={task.start_date}
            isDisputeAllowed={isDisputeAllowed}
            accepted={isAccepted}
            pending={isPending}
            isPayWaiting={isPayWaiting}
            payDeadLine={task.pay_deadline}
            disputeDeadLine={task.dispute_deadline}
            acceptDeadLine={task.accept_deadline}
            endDate={task.end_date}
          />
        }
        Interface={
          <TaskInterface
            buttonsAppear={buttonsAppear}
            t={t}
            copyIdToClipboard={copyIdToClipboard}
            openEditor={() => props.openEditor(task)}
            hasReview={Boolean(task.review)}
            removeMut={removeMut}
            finishMut={finishMut}
            acceptMut={acceptMut}
            payLinkMut={payLinkMut}
            requestReviewMut={requestReviewMut}
            requestPhotoMut={requestPhotoMut}
            copyMut={copyMut}
            sendMessage={sendMessage}
            note={note}
            isTaskConsumer={isTaskConsumer}
            toggleAction={toggleAction}
            openWarehouseTxnEditor={openWarehouseTxnEditor}
            remind={remind}
            requestPayout={requestPayout}
            removeDisputeMut={removeDisputeMut}
          />
        }
        ClientExcerpt={
          props.viewMode !== "client" &&
          !task.personal && (
            <ClientExcerpt
              isTaskMaster={isTaskMaster}
              task={task}
              openClientProfile={openClientProfile}
              isTaskConsumer={isTaskConsumer}
            />
          )
        }
      />
      <TaskActions
        locale={props.locale}
        currency={props.currency}
        onClose={closeAction}
        t={t}
        isTaskMaster={isTaskMaster}
        isTaskConsumer={isTaskConsumer}
        createNote={createNote}
        removeNote={removeNote}
        onCancel={cancelTask}
        task={task}
        active={activeAction}
      />
    </>
  );
};
TaskItem.propTypes = {
  authUserId: PT.string.isRequired,
  authMasterId: PT.string,
  currency: PT.string.isRequired,
  task: PT.object.isRequired,
  locale: PT.string.isRequired,
  className: PT.string,
  viewMode: PT.oneOf(["scheduler", "client"]),

  onTaskUpdate: PT.func,
  onTaskRemove: PT.func,
  onTaskCancel: PT.func,
  sendExternalNotification: PT.func.isRequired,
  sendMessage: PT.func.isRequired,
  t: PT.func.isRequired,
  openClientProfile: PT.func,
  openEditor: PT.func.isRequired,
};

export default connector(TaskItem);
